//
// ChatPic - Component for displaying image messages with zoom and save functionality
// Handles picture display in chat with overlay zoom, download capability, and notes
//

import PropTypes from 'prop-types';
import {
  useState,
  useEffect,
  memo,
  useMemo,
  useRef,
} from 'react';

import { useDisplay } from '../../../../stores';
import { Button } from '../../../buttons';
import { ImagePanAndZoom, Row } from '../../../display';
import { useIsMounted } from '../../../React';

const areEqual = (prev, next) => {
  let diff;
  if ((!diff) && (prev.csid !== next.csid)) diff = 'csid';
  if ((!diff) && (prev.msgId !== next.msgId)) diff = 'msgId';
  if ((!diff) && (prev.qstnId !== next.qstnId)) diff = 'qstnId';

  if (diff !== undefined) {
    console.log(`ChatPic refresh src=${diff}`);
    return false;
  }

  return true;
};

const ChatPic = memo(
  (props) => {
    const {
      translate,
      message,
      getMedia,
      csid,
      piid,
      refreshCellSize,
    } = props;
    const {
      qstnId,
      note,
      qstnTxt,
      unknown,
      fileInfo,
      msgId,
    } = message;
    const [pic, setPic] = useState((csid)
      ? getMedia({
          csid, piid, msgId, type: 'img',
        })
      : null);
    const isMountedRef = useIsMounted();
    const { display } = useDisplay();
    const {
      setOverlay,
    } = display;

    const displayHeight = useMemo(
      () => ((fileInfo)
        ? `${Math.min(fileInfo.height, 160)}px`
        : '100px'),
      [fileInfo],
    );

    // Re-measuring re-renders the row, which reloads the picture on some browsers: measure once
    const sizedRef = useRef(false);
    const onPicLoad = () => {
      if (sizedRef.current) return;
      sizedRef.current = true;
      refreshCellSize();
    };

    const savePic = () => {
      // Create a temporary <a> element
      const link = document.createElement('a');
      link.href = pic;

      // Setting the download attribute gives a suggested filename
      link.setAttribute(
        'download',
        `paxity_${msgId}`,
      );

      // Append it to the body (required for the click to work in some browsers)
      document.body.appendChild(link);

      // Trigger the download
      link.click();

      // Clean up
      document.body.removeChild(link);
    };

    const onClick = () => {
      setOverlay(
        <ImagePanAndZoom
          className="z-2000 backdrop-blur-sm w-screen fixed top-0 left-0 h-full w-full bg-black/60 py-2"
          onClick={() => { setOverlay(); }}
          src={pic}
          enable={false}
        >

          <Button
            imgSrc="/images/svg/gui/cross.svg"
            noFrame
            imgColor="#FFFFFF"
            absolute
            className="top-3 right-3 fill-white mix-blend-difference"
            onClick={() => { setOverlay(); }}
            imgColorAsCssFill
            small={false}
          />

          <Row absolute fullw className="absolute bottom-0 pb-5">
            <Button
              label={translate('save')}
              onClick={savePic}
              baseColor
            />
          </Row>

        </ImagePanAndZoom>,
      );
    };

    // Remove overlay when unmounted:
    useEffect(
      () => () => { setOverlay(); },
      [setOverlay],
    );

    useEffect(
      () => {
        if ((!pic) && csid) {
          getMedia(
            {
              csid,
              piid,
              msgId,
              type: 'img',
            },
            (err, newPic) => {
              if ((!err) && isMountedRef.current) {
                setPic(newPic);
              }
            },
          );
        }
      },
      [pic, csid, piid, getMedia, msgId, isMountedRef],
    );

    if (unknown) {
      return (
        <div className="w-full">
          <p className="text-left text-base mb-4">
            <i>
              <b>
                {`${translate(qstnId, 'questionnaireTitles')}`}
                {(qstnTxt) ? ` ${qstnTxt}: ` : ': '}
              </b>
              {translate('impossible')}
            </i>
          </p>
        </div>
      );
    }

    return (
      <div className="w-full">
        {(qstnId)
          ? (
              <p className="text-left text-base mb-2">
                <i>
                  <b>
                    {translate(qstnId, 'questionnaireTitles')}
                    {(qstnTxt) ? ` ${qstnTxt}:` : ':'}
                  </b>
                </i>
              </p>
            )
          : null}

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          className="max-h-40 max-w-full relative overflow-hidden p-0 mb-2 flex justify-center items-center border border-black/10 rounded-md bg-pxPicBackdrop cursor-pointer"
          style={{ height: displayHeight }}
          onClick={onClick}
        >
          <img
            alt=""
            src={pic}
            className="max-h-40 max-w-full"
            // Update the message size in cache once the pic has loaded
            onLoad={onPicLoad}
          />
        </div>

        {(note)
          ? (
              <div className="max-w-full break-words whitespace-normal p-2">
                <div className="bg-pxBgMuted p-2 rounded-md border shadow-sm">
                  <u>{`${translate('notes')}:`}</u>
                  <br />
                  <p className="pl-2.5 mt-1 mb-0">
                    {note}
                  </p>
                </div>
              </div>
            )
          : null}

      </div>
    );
  },
  areEqual,
);
ChatPic.displayName = 'ChatPic';

ChatPic.propTypes = {
  translate: PropTypes.func.isRequired,
  message: PropTypes.shape({
    id: PropTypes.number.isRequired,
    msgId: PropTypes.number.isRequired,
    qstnId: PropTypes.string.isRequired,
    qstnTxt: PropTypes.string,
    note: PropTypes.string,
    unknown: PropTypes.bool,
    fileInfo: PropTypes.shape({
      height: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      // durationTS: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  getMedia: PropTypes.func.isRequired,
  csid: PropTypes.string.isRequired,
  piid: PropTypes.string,
  refreshCellSize: PropTypes.func.isRequired,
};

export default ChatPic;
